import * as React from "react";
import jpg_images from "url:../../images/*.jpg";
import mp4_videos from "url:../../videos/*.mp4";
import { topData } from "../data";
import { getAssetUrl, resolveAssetMap } from "../asset";
import { getLocalizedText, yourNameEn, yourNameJa } from "../util";
import { useLang } from "../lang";

const Hero: React.FC = () => {
  const { langEn } = useLang();
  const shellRef = React.useRef<HTMLElement | null>(null);
  const images = resolveAssetMap(jpg_images);
  const videos = resolveAssetMap(mp4_videos);
  const hero = topData.hero || {};
  const heroVideoKey = hero.video || "";
  const heroVideoUrl = getAssetUrl(videos, heroVideoKey);
  const heroImageKey = hero.image || "";
  const heroMobile = Array.isArray(hero.mobile) ? hero.mobile : [];
  const heroImageMobileTopKey =
    heroMobile[0] || hero.imageMobileTop || hero.imageMobile || heroImageKey;
  const heroImageMobileBottomKey =
    heroMobile[1] ||
    hero.imageMobile ||
    hero.imageMobileTop ||
    heroImageMobileTopKey ||
    heroImageKey;
  const heroImageUrl = getAssetUrl(images, heroImageKey);
  const heroImageMobileTopUrl = getAssetUrl(images, heroImageMobileTopKey) || heroImageUrl;
  const heroImageMobileBottomUrl = getAssetUrl(images, heroImageMobileBottomKey) || heroImageUrl;
  const heroImageStyle = heroImageUrl ? { backgroundImage: `url(${heroImageUrl})` } : undefined;
  const heroImageMobileTopStyle = heroImageMobileTopUrl
    ? { backgroundImage: `url(${heroImageMobileTopUrl})` }
    : heroImageStyle;
  const heroImageMobileBottomStyle = heroImageMobileBottomUrl
    ? { backgroundImage: `url(${heroImageMobileBottomUrl})` }
    : heroImageStyle;
  const heroTaglineText = getLocalizedText(topData.heroTagline, langEn);

  React.useEffect(() => {
    const target = shellRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const heroDominant = entry.intersectionRatio > 0.45;
        document.body.classList.toggle("hero-in-view", heroDominant);
      },
      { threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 1] }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
      document.body.classList.remove("hero-in-view");
    };
  }, []);

  return (
    <section id="top" className="ftco-section top-hero-shell" ref={shellRef}>
      <div className="top-hero-full">
        {heroVideoUrl ? (
          <>
            <video
              className="top-hero-video"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={heroImageUrl || undefined}
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>
            <div className="top-hero-mobile-split">
              <div
                className="top-hero-mobile-panel top-hero-mobile-top-image"
                style={heroImageMobileTopStyle}
              ></div>
              <div
                className="top-hero-mobile-panel top-hero-mobile-bottom-image"
                style={heroImageMobileBottomStyle}
              ></div>
            </div>
          </>
        ) : (
          <div className="top-hero-image" style={heroImageStyle}></div>
        )}
        <div className="top-hero-overlay"></div>
      </div>
      <div className="top-hero-content">
        <div className="container top-hero-inner">
          <p className="mb-0 h2 d-none d-md-block top-hero-name">
            {yourNameEn}
            <span className="ml-2">| {yourNameJa}</span>
          </p>
          {heroTaglineText ? (
            <p className="d-none d-md-block top-hero-tagline">{heroTaglineText}</p>
          ) : (
            ""
          )}

          <p className="mb-0 d-sm-none h3 top-hero-name">{yourNameEn}</p>
          <p className="mb-2 d-sm-none h4 top-hero-name">{yourNameJa}</p>
          {heroTaglineText ? (
            <p className="d-md-none top-hero-mobile-tagline">{heroTaglineText}</p>
          ) : (
            ""
          )}
          <div
            className="h6 mb-2 top-hero-sub d-none d-md-block"
            dangerouslySetInnerHTML={{
              __html: getLocalizedText(topData.selfIntroduction, langEn),
            }}
          ></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
